/**
 * Collapsed day-card summary for the family grid — one row per child: a colored
 * avatar + name, then compact thumbnails of that child's meals for the day.
 * Submitted (ordered) meals carry a GREEN quantity badge; meals only staged in
 * the cart (not yet ordered) carry a WHITE/muted badge, so a parent can tell
 * what's placed from what's pending at a glance. If a child has items but is
 * missing one of the day's mealtimes (a mealtime another sibling ordered in),
 * a small warning lists each missed mealtime. Read-only.
 *
 * While the day's order window is open and a child has nothing yet, that
 * child's row shows the day's actual menu instead of a dead "No meals yet"
 * (PROD-12): an "On the menu" strip of item photos + names, so the week view
 * sells the food before anything is ordered. The strip renders per child (not
 * per day) because siblings can be served different menus.
 */

import { UtensilsCrossed } from 'lucide-react'
import { type ReactElement, useMemo } from 'react'

import type { MenuWeekPreviewDay } from '@/api/orders'
import type { OrderingStudent } from '@/api/orders-family'
import type { Mealtime } from '@/api/school-orders'
import { AssetImage } from '@/components/asset-image'
import { OnTheMenuStrip } from '@/components/orders/on-the-menu-strip'
import { UserAvatar } from '@/components/user-identity-card'
import type { FamilyAlreadyOrdered, OrderedItemSummary } from '@/lib/family-orders/already-ordered'
import { autoOrderSkippedMealtimes } from '@/lib/family-orders/auto-order-skips'
import { gradeBandUnmappedMessage, isBandBlockedStudent } from '@/lib/family-orders/no-menu-reason'
import { MEALTIME_LABELS, sortMealtimes } from '@/lib/school-orders/mealtime'
import { selectLinesFor, useFamilyCart } from '@/stores/family-cart'

import { useAutoOrderCoverage } from './auto-order-coverage'
import { childLocationRow } from './columns'

type Tone = 'ordered' | 'cart'

function ItemThumb({ item, tone }: { item: OrderedItemSummary; tone: Tone }): ReactElement {
  const badge =
    tone === 'ordered' ? 'bg-emerald-600 text-white' : 'bg-background text-foreground shadow ring-1 ring-border'
  return (
    <div
      className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-muted"
      title={`${item.quantity} × ${item.title}${tone === 'cart' ? ' (in cart)' : ''}`}>
      <AssetImage
        src={item.imageUri}
        alt={item.title}
        className="h-full w-full object-cover"
        fallback={
          <span className="grid h-full w-full place-items-center">
            <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
          </span>
        }
      />
      <span
        className={`absolute bottom-1 right-1 grid h-5 min-w-5 place-items-center rounded-full px-1 text-[11px] font-semibold tabular-nums ${badge}`}>
        {item.quantity}
      </span>
    </div>
  )
}

export interface FamilyCollapsedSummaryProps {
  dateKey: string
  locationId: string
  columns: OrderingStudent[]
  alreadyOrdered: FamilyAlreadyOrdered
  /** Ordering window for this day has closed — changes the empty legend copy. */
  closed?: boolean
  /**
   * The school isn't serving this day at all (blocked date, weekday off the
   * service days, out of session). Distinct from `closed`: nothing was missed,
   * so the legend reads "No service" rather than "No meals ordered".
   */
  noService?: boolean
  /**
   * The campus takes no parent orders (school ordering only, PROD-1554): the
   * menu teaser is the content, and nothing reads as a missed order or an
   * auto-order nudge.
   */
  viewOnly?: boolean
  /** The day's menu teaser — shown while a child has nothing ordered/staged. */
  menuPreview?: MenuWeekPreviewDay | null
  /**
   * Campus caption under each child's name (pin + name). Set when the family
   * spans more than one campus so rows stay distinguishable without a
   * section header.
   */
  campusName?: string | null
}

export function FamilyCollapsedSummary({
  dateKey,
  locationId,
  columns,
  alreadyOrdered,
  closed = false,
  noService = false,
  viewOnly = false,
  menuPreview = null,
  campusName = null,
}: FamilyCollapsedSummaryProps): ReactElement {
  const cartLines = useFamilyCart(selectLinesFor(locationId))
  // Cart-staged items per child for this day, summed by menu item, plus the set
  // of mealtimes each child has cart items in.
  const cart = useMemo(() => {
    const items = new Map<string, OrderedItemSummary[]>()
    const mealtimes = new Map<string, Set<Mealtime>>()
    for (const line of cartLines) {
      if (line.date !== dateKey || line.quantity <= 0) {
        continue
      }
      const list = items.get(line.studentMemberId) ?? []
      const existing = list.find((i) => i.menuItemId === line.menuItemId)
      if (existing) {
        existing.quantity += line.quantity
      } else {
        list.push({ menuItemId: line.menuItemId, title: line.name, imageUri: line.imageUri, quantity: line.quantity })
      }
      items.set(line.studentMemberId, list)
      const mts = mealtimes.get(line.studentMemberId) ?? new Set<Mealtime>()
      mts.add(line.mealtime)
      mealtimes.set(line.studentMemberId, mts)
    }
    return { items, mealtimes }
  }, [cartLines, dateKey])

  // The mealtimes each child has anything in, and the day-level union across all
  // children — used to flag the mealtimes a child with items missed.
  const childMealtimes = useMemo(() => {
    const map = new Map<string, Set<Mealtime>>()
    for (const child of columns) {
      const set = new Set<Mealtime>(alreadyOrdered.itemsForChildOnDayByMealtime(dateKey, child.userId).keys())
      for (const mealtime of cart.mealtimes.get(child.accountMemberId) ?? []) {
        set.add(mealtime)
      }
      map.set(child.accountMemberId, set)
    }
    return map
  }, [columns, alreadyOrdered, dateKey, cart.mealtimes])
  const dayMealtimes = useMemo(
    () => sortMealtimes(new Set([...childMealtimes.values()].flatMap((set) => [...set]))),
    [childMealtimes],
  )

  const teaser = !closed && menuPreview && menuPreview.items.length > 0 ? menuPreview : null

  // When SOME siblings auto-order and this child doesn't, their empty state
  // must say so — the auto-ordering siblings' meals will appear on their own,
  // and this child's won't. Only surfaced in the mixed state: with auto-order
  // fully off, the week-level nudge card already carries the message.
  const coverage = useAutoOrderCoverage(columns, locationId)
  const mixedCoverage = coverage?.state === 'mixed' ? coverage : null
  // PROD-1037: when a child's enabled auto-order stood down because the slot
  // was already hand-ordered, say so — a silent skip reads as "auto-order
  // broke". Empty when the auto-order itself placed (nothing was skipped).
  const skippedMealtimesFor = (child: OrderingStudent): Mealtime[] =>
    autoOrderSkippedMealtimes({
      dateKey,
      studentUserId: child.userId,
      subscription: coverage?.subByUserId.get(child.userId),
      alreadyOrdered,
    })

  return (
    <div className="flex flex-col gap-9">
      {columns.map((child) => {
        const ordered = alreadyOrdered.itemsForChildOnDay(dateKey, child.userId)
        const cartItems = cart.items.get(child.accountMemberId) ?? []
        const hasAny = ordered.length > 0 || cartItems.length > 0
        const own = childMealtimes.get(child.accountMemberId) ?? new Set<Mealtime>()
        // A view-only campus never asks for an order, so no mealtime reads as missed.
        const missed = hasAny && !viewOnly ? dayMealtimes.filter((mealtime) => !own.has(mealtime)) : []
        const autoOrderNote =
          !viewOnly && !closed && !hasAny && mixedCoverage && !mixedCoverage.activeUserIds.has(child.userId) ? (
            <span className="text-xs text-amber-600 dark:text-amber-500">
              Auto-order not enabled — place orders manually.
            </span>
          ) : null
        // Fail-closed grade-band gate (2026-07 flip): a student whose grade
        // has no band mapping gets no menu teaser (all-band-less campuses
        // skip the fetch) and no menus on the gated surfaces. Swap the
        // dead-end "No meals yet" for a self-diagnosing hint — per student,
        // since a sibling with a mapped band can be ordering fine right
        // beside them. It also wins over the auto-order note: ordering
        // advice is moot while the gate blocks the child.
        const bandHint =
          !closed &&
          !hasAny &&
          isBandBlockedStudent({
            displayName: child.displayName,
            isSelf: child.isSelf,
            location: childLocationRow(child, locationId),
          }) ? (
            <span className="text-xs text-muted-foreground">{gradeBandUnmappedMessage(child.displayName)}</span>
          ) : null
        return (
          <div key={child.accountMemberId} className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <UserAvatar
                name={child.displayName}
                initials={child.initials}
                avatarUri={child.avatarUri}
                avatarColor={child.avatarColor}
                size="md"
              />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-base font-medium leading-tight text-foreground">
                  {child.displayName}
                </span>
                {campusName ? (
                  <span className="truncate text-xs leading-tight text-muted-foreground">{campusName}</span>
                ) : null}
              </div>
            </div>
            {hasAny ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  {ordered.map((item) => (
                    <ItemThumb key={`o-${item.menuItemId}`} item={item} tone="ordered" />
                  ))}
                  {cartItems.map((item) => (
                    <ItemThumb key={`c-${item.menuItemId}`} item={item} tone="cart" />
                  ))}
                </div>
                {missed.map((mealtime) => (
                  <span key={mealtime} className="text-xs text-amber-600 dark:text-amber-500">
                    No meals ordered for {MEALTIME_LABELS[mealtime]}
                  </span>
                ))}
                {skippedMealtimesFor(child).map((mealtime) => (
                  <span key={`auto-skip-${mealtime}`} className="text-xs text-muted-foreground">
                    Auto-order skipped for {MEALTIME_LABELS[mealtime].toLowerCase()}. An order was already placed.
                  </span>
                ))}
              </div>
            ) : teaser ? (
              <div className="flex flex-col gap-2">
                <OnTheMenuStrip preview={teaser} />
                {autoOrderNote}
              </div>
            ) : // A no-service day wins outright: the band hint and the
            // auto-order nudge both describe missed ordering, and nothing was
            // missed on a day the school never serves.
            noService ? (
              <span className="text-xs text-muted-foreground">No service</span>
            ) : (
              (bandHint ??
              autoOrderNote ?? (
                <span className="text-xs text-muted-foreground">{emptySummaryLabel({ viewOnly, closed })}</span>
              ))
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * The empty-row legend when a child has nothing ordered and no menu teaser.
 * A view-only campus has nothing to order, so the only honest empty state is
 * that no menu is up; ordering campuses keep their closed/open wording.
 */
export function emptySummaryLabel({ viewOnly, closed }: { viewOnly: boolean; closed: boolean }): string {
  if (viewOnly) {
    return 'No menu posted.'
  }
  return closed ? 'No meals ordered' : 'No meals yet'
}
